import { assertIsNotUndefined } from "../utils/assertOfType";
import { EntityRelationship } from "./entityGraphTypes";

export class EntityGraph<N extends object, R extends string> {
  /**
   * Map of nodes by hash provided by hashFn
   */
  private readonly _nodes: Map<string, WeakRef<N>> = new Map();
  private readonly _edges: Map<string, Map<string, R>> = new Map();

  get nodes(): N[] {
    return Array.from(this._nodes.values())
      .map((ref) => ref?.deref())
      .filter(notUndefinedOrNull);
  }

  constructor(private readonly hashFn: (entity: N) => string) {}

  add(entity: N): N {
    const hash = this.hashFn(entity);
    if (this.has(entity)) {
      const existingNode = this.nodeByHash(hash);
      assertIsNotUndefined(existingNode, "Node deletion race condition detected");
      return existingNode;
    }
    const newRef = new WeakRef(entity);
    this._nodes.set(hash, newRef);
    return entity;
  }

  has(entity: N): boolean {
    const hash = this.hashFn(entity);
    return this._nodes.get(hash)?.deref() !== undefined;
  }

  delete(entity: N): void {
    const hash = this.hashFn(entity);
    const neighbors = this._edges.get(hash);
    if (neighbors) {
      for (const neighborHash of neighbors.keys()) {
        this._edges.get(neighborHash)?.delete(hash);
      }
    }
    this._nodes.delete(hash);
    this._edges.delete(hash);
  }

  relate(a: N, type: R, b: N, bidirectional: boolean = true): string {
    const aHash = this.hashFn(a);
    const bHash = this.hashFn(b);
    if (this._edges.get(aHash)?.has(bHash)) {
      return bHash;
    }
    if (!this._edges.has(aHash)) {
      this._edges.set(aHash, new Map());
    }
    this._edges.get(aHash)?.set(bHash, type);
    if (bidirectional) {
      this.relate(b, type, a, false);
    }
    return bHash;
  }

  node(entity: N): N {
    const hash = this.hashFn(entity);
    let node = this.nodeByHash(hash);
    if (!node) {
      node = this.add(entity);
    }
    return node;
  }

  relationships(
    entity: N,
    predicate?: (relationship: EntityRelationship<string, R>) => boolean,
  ): EntityRelationship<string, R>[] {
    const hash = this.hashFn(entity);
    return Array.from(this._edges.get(hash)?.entries() ?? []).reduce<EntityRelationship<string, R>[]>(
      (acc, [neighborHash, type]) => {
        const relationship = new EntityRelationship(type, hash, neighborHash);
        if (predicate && !predicate(relationship)) {
          return acc;
        }
        acc.push(relationship);
        return acc;
      },
      [] as EntityRelationship<string, R>[],
    );
  }

  nodeByHash(hash: string): N | undefined {
    return this._nodes.get(hash)?.deref();
  }

  breadthFirstSearch(root: N, depth: number = 3): N[] {
    if (depth < 0) {
      return [];
    }

    const notRoot = notNode(root);

    const results = new Set<N>();
    const visited = new WeakMap<N, boolean>();
    const queue: Array<[N, number]> = [];

    // Start with the root node at depth 0
    visited.set(root, true);
    queue.push([root, 0]);

    while (queue.length > 0) {
      const item = queue.shift();
      if (!item) {
        continue;
      }
      const [node, currentDepth] = item;

      // Stop if we've reached the maximum depth
      if (currentDepth >= depth) {
        continue;
      }

      // Get neighbors of the current node
      const neighbors = this.graphNeighbors(node);

      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.set(neighbor, true);
          if (notRoot(neighbor)) {
            results.add(neighbor);
          }
          queue.push([neighbor, currentDepth + 1]);
        }
      }
    }

    return Array.from(results);
  }

  private graphNeighbors(node: N): N[] {
    return this.relationships(node)
      .map((r) => this.nodeByHash(r.b))
      .filter(notUndefinedOrNull);
  }
}

export function notNode<N>(node: N): (n: N) => boolean {
  return (n: N) => n !== node;
}

export function notUndefinedOrNull<T>(item: T | undefined | null): item is T {
  return item !== undefined && item !== null;
}
